import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Inbox, Mail, Disc3, FileEdit, ImageDown } from "lucide-react";
import { fetchSubmissions } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import EpisodeManager from "@/components/EpisodeManager";
import ContentManager from "@/components/ContentManager";
import PressManager from "@/components/PressManager";

const KIND_STYLES = {
    question: "bg-mustard/40 text-ink",
    story: "bg-pink/15 text-pink",
    press: "bg-ink text-paper",
};

export default function HostInbox() {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const [submissions, setSubmissions] = useState(null);
    const [tab, setTab] = useState("inbox");

    useEffect(() => {
        if (!user) return;
        fetchSubmissions()
            .then(setSubmissions)
            .catch(() => setSubmissions([]));
    }, [user]);

    if (user === null) {
        return (
            <div data-testid="host-loading" className="min-h-[60vh] flex items-center justify-center">
                <p className="font-sub font-bold text-ink/60">Checking your session…</p>
            </div>
        );
    }
    if (user === false) return <Navigate to="/login" replace />;

    const handleLogout = async () => {
        await signOut();
        navigate("/", { replace: true });
    };

    const counts = (submissions || []).reduce((acc, s) => {
        acc[s.kind] = (acc[s.kind] || 0) + 1;
        return acc;
    }, {});

    return (
        <div data-testid="host-inbox-page" className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
            <Reveal className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                        Signed in as {user.email}
                    </p>
                    <h1 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-ink tracking-tight">
                        The host desk
                    </h1>
                </div>
                <button
                    data-testid="host-logout-btn"
                    onClick={handleLogout}
                    className="inline-flex items-center gap-2 bg-cream text-ink font-sub font-bold text-sm px-5 py-3 border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
                >
                    <LogOut className="w-4 h-4" /> Sign out
                </button>
            </Reveal>

            <Reveal delay={0.1}>
                <div className="mt-8 flex flex-wrap gap-3" role="tablist" aria-label="Host sections">
                    {[
                        { id: "inbox", label: "Inbox", Icon: Inbox },
                        { id: "episodes", label: "Episodes", Icon: Disc3 },
                        { id: "content", label: "Site content", Icon: FileEdit },
                        { id: "press", label: "Press kit", Icon: ImageDown },
                    ].map(({ id, label, Icon }) => (
                        <button
                            key={id}
                            role="tab"
                            aria-selected={tab === id}
                            data-testid={`host-tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`inline-flex items-center gap-2 font-sub font-bold text-sm px-5 py-3 border-2 border-ink transition-colors duration-150 ${
                                tab === id ? "bg-ink text-paper hard-shadow-sm" : "bg-cream text-ink hover:bg-mustard/30"
                            }`}
                        >
                            <Icon className="w-4 h-4" /> {label}
                        </button>
                    ))}
                </div>
            </Reveal>

            {tab === "episodes" ? (
                <div className="mt-10">
                    <EpisodeManager />
                </div>
            ) : tab === "content" ? (
                <div className="mt-10">
                    <ContentManager />
                </div>
            ) : tab === "press" ? (
                <div className="mt-10">
                    <PressManager />
                </div>
            ) : (
                <>
                    <Reveal delay={0.15}>
                        <p className="mt-6 text-sm text-ink/70 flex flex-wrap gap-4" data-testid="inbox-counts">
                            <span>{counts.question || 0} questions</span>
                            <span>{counts.story || 0} stories</span>
                            <span>{counts.press || 0} press inquiries</span>
                        </p>
                    </Reveal>
                    <div className="mt-8 space-y-6" data-testid="submissions-list">
                        {submissions === null ? (
                            <p className="font-sub font-bold text-ink/60">Loading the mail…</p>
                        ) : submissions.length === 0 ? (
                            <div className="bg-cream border-2 border-ink hard-shadow-sm p-10 text-center">
                                <Inbox className="w-8 h-8 mx-auto text-ink/40" />
                                <p data-testid="submissions-empty" className="mt-4 font-sub font-bold text-ink/60">
                                    Nothing yet. Questions and stories will land here.
                                </p>
                            </div>
                        ) : (
                            submissions.map((s, i) => (
                                <Reveal key={s.id} delay={Math.min(i * 0.05, 0.3)}>
                                    <article
                                        data-testid={`submission-${s.id}`}
                                        className={`bg-cream border-2 border-ink hard-shadow-sm p-6 sm:p-7 ${
                                            i % 2 ? "rotate-[0.5deg]" : "-rotate-[0.5deg]"
                                        }`}
                                    >
                                        <div className="flex flex-wrap items-center gap-3">
                                            <span
                                                className={`font-mono-brand text-xs uppercase tracking-[0.15em] px-3 py-1 border border-ink/20 ${KIND_STYLES[s.kind] || ""}`}
                                            >
                                                {s.kind}
                                            </span>
                                            <span className="font-sub font-bold text-ink">{s.name}</span>
                                            {s.email && (
                                                <a
                                                    href={`mailto:${s.email}`}
                                                    className="inline-flex items-center gap-1 text-sm text-pink font-sub font-bold"
                                                >
                                                    <Mail className="w-3.5 h-3.5" /> {s.email}
                                                </a>
                                            )}
                                            <span className="ml-auto font-mono-brand text-xs text-ink/50">
                                                {new Date(s.created_at).toLocaleString()}
                                            </span>
                                        </div>
                                        <p className="mt-4 text-sm sm:text-base leading-relaxed text-ink/85 whitespace-pre-wrap">
                                            {s.message}
                                        </p>
                                    </article>
                                </Reveal>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
